import React, { useEffect, useRef, useMemo } from 'react';
import { View, Text, StyleSheet, Animated, Dimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { androidHeaderTopInset } from '../../shared/theme/sharedStyles';
import { DEFAULT_TOAST_DURATION_MS } from '../toast/toastStore';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

/** Must sit above sticky/absolute chrome (e.g. MenuFlowHeader zIndex 1e6) so the message is visible. */
const TOAST_OVERLAY_Z_INDEX = 2_000_000;

export type ToastVariant = 'default' | 'success' | 'error';

interface ToastProps {
  message: string;
  visible: boolean;
  onHide: () => void;
  /** Defaults to `DEFAULT_TOAST_DURATION_MS` from `toastStore` (visible time, excluding enter/exit animation). */
  duration?: number;
  variant?: ToastVariant;
}

function createToastStyles(c: ThemeColors) {
  return StyleSheet.create({
    overlay: {
      position: 'absolute',
      left: 0,
      top: 0,
      backgroundColor: 'transparent',
      zIndex: TOAST_OVERLAY_Z_INDEX,
      elevation: TOAST_OVERLAY_Z_INDEX,
    },
    container: {
      position: 'absolute',
      left: appTheme.spacing.md,
      right: appTheme.spacing.md,
      alignItems: 'center',
    },
    toast: {
      backgroundColor: c.text,
      paddingHorizontal: appTheme.spacing.lg,
      paddingVertical: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.lg,
      maxWidth: SCREEN_WIDTH - appTheme.spacing.xl,
      ...appTheme.shadows.md,
      shadowColor: c.shadowColor,
    },
    toastSuccess: {
      backgroundColor: c.success,
    },
    toastError: {
      backgroundColor: c.error,
    },
    message: {
      color: c.surface,
      ...appTheme.typography.bodyMedium,
      textAlign: 'center',
    },
    messageOnSuccess: {
      color: c.onSuccess,
    },
    messageOnError: {
      color: c.onError,
    },
  });
}

export const Toast: React.FC<ToastProps> = ({ message, visible, onHide, duration = DEFAULT_TOAST_DURATION_MS, variant = 'default' }) => {
  const insets = useSafeAreaInsets();
  const translateY = useRef(new Animated.Value(-100)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createToastStyles(colors), [colors]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    let cancelled = false;

    Animated.parallel([
      Animated.spring(translateY, {
        toValue: 0,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start();

    const timer = setTimeout(() => {
      if (cancelled) {
        return;
      }
      Animated.parallel([
        Animated.timing(translateY, {
          toValue: -100,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start(({ finished }) => {
        if (finished && !cancelled) {
          onHide();
        }
      });
    }, duration);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      translateY.stopAnimation();
      opacity.stopAnimation();
    };
  }, [visible, message, duration, onHide, translateY, opacity]);

  if (!visible) {
    return null;
  }

  const toast_style = [styles.toast, variant === 'success' && styles.toastSuccess, variant === 'error' && styles.toastError];
  const message_style = [styles.message, variant === 'success' && styles.messageOnSuccess, variant === 'error' && styles.messageOnError];

  return (
    <View style={[styles.overlay, { width: SCREEN_WIDTH, height: SCREEN_HEIGHT }]} pointerEvents="box-none" collapsable={false}>
      <Animated.View
        style={[
          styles.container,
          {
            top: androidHeaderTopInset(insets.top) + appTheme.spacing.sm,
            transform: [{ translateY }],
            opacity,
          },
        ]}
        pointerEvents="none">
        <View style={toast_style} pointerEvents="none">
          <Text style={message_style}>{message}</Text>
        </View>
      </Animated.View>
    </View>
  );
};
